;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import type { JwtUser } from '@workspace/ui/types';
import { jwtDecode } from 'jwt-decode';

type PermissionMap = Record<string, number>;

type CachedData = {
  data: PermissionMap;
  expiresAt: number; // Timestamp when cache expires
};

let cachedPermissionMap: CachedData | null = null;
// Add promise tracking to prevent race conditions
let fetchPromise: Promise<PermissionMap> | null = null;

let isAppRouterCached: boolean | null = null;

// Check if we're in an App Router environment
async function isAppRouter() {
  if (isAppRouterCached !== null) {
    return isAppRouterCached;
  }

  try {
    await import('next/headers');
    isAppRouterCached = true;
    return true;
  } catch {
    isAppRouterCached = false;
    return false;
  }
}

export async function fetchPermissionMap() {
  // Do not use Next's Data Cache here. `force-cache` persists across `next dev`
  // restarts, so a permissions.json fetched before an SSO seed keeps missing
  // newly added names while JWTs already contain the new ids.
  const res = await fetch(`${process.env.NEXT_PUBLIC_SSO_API_URL}/storage/permissions.json`, {
    cache: 'no-store',
  });
  if (!res.ok) throw new Error('Failed to load permission map');
  return (await res.json()) as PermissionMap;
}

export async function getPermissionMap() {
  const now = Date.now();

  // Check if cache exists and hasn't expired
  if (cachedPermissionMap && now < cachedPermissionMap.expiresAt) {
    return cachedPermissionMap.data;
  }

  // If a fetch is already in progress, wait for it
  if (fetchPromise) {
    try {
      const map = await fetchPromise;
      return map;
    } catch {
      // If the ongoing fetch fails, we'll try again below
      fetchPromise = null;
    }
  }

  // Start a new fetch and store the promise
  fetchPromise = fetchPermissionMap();

  try {
    const map = await fetchPromise;

    cachedPermissionMap = {
      data: map,
      expiresAt: now + 60 * 60 * 1000, // TTL: 1 hour from now
    };

    return map;
  } catch (error) {
    console.error('Error fetching permission map:', error);

    // Serve stale data if available
    if (cachedPermissionMap) {
      console.warn('Using stale permission map due to fetch error');
      return cachedPermissionMap.data;
    }
    throw error;
  } finally {
    // Clear the promise so future requests can retry
    fetchPromise = null;
  }
}

// Server-side permission utilities (App Router only)
export async function getUserFromToken(): Promise<JwtUser | null> {
  if (!(await isAppRouter())) {
    throw new Error('getUserFromToken() only works in App Router server components');
  }

  try {
    const { cookies } = await import('next/headers');
    const cookieStore = await cookies();
    const accessToken = cookieStore.get('access_token')?.value;

    if (!accessToken) {
      return null;
    }

    const decodedToken = jwtDecode<JwtUser>(accessToken);
    return decodedToken;
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
}

export async function hasPermission(permissionName: string): Promise<boolean> {
  try {
    const [user, permissionMap] = await Promise.all([getUserFromToken(), getPermissionMap()]);

    if (!user?.permissions || !permissionMap) {
      return false;
    }

    const permissionId = permissionMap[permissionName];

    if (!permissionId) {
      console.warn(`Permission "${permissionName}" not found in permission map`);
      return false;
    }

    return user.permissions.includes(permissionId);
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
}

export async function hasAnyPermission(permissionNames: string[]): Promise<boolean> {
  try {
    const [user, permissionMap] = await Promise.all([getUserFromToken(), getPermissionMap()]);

    if (!user?.permissions || !permissionMap) {
      return false;
    }

    return permissionNames.some((permissionName) => {
      const permissionId = permissionMap[permissionName];
      return permissionId && user.permissions.includes(permissionId);
    });
  } catch (error) {
    console.error('Error checking permissions:', error);
    return false;
  }
}

export async function hasAllPermissions(permissionNames: string[]): Promise<boolean> {
  try {
    const [user, permissionMap] = await Promise.all([getUserFromToken(), getPermissionMap()]);

    if (!user?.permissions || !permissionMap) {
      return false;
    }

    return permissionNames.every((permissionName) => {
      const permissionId = permissionMap[permissionName];
      return permissionId && user.permissions.includes(permissionId);
    });
  } catch (error) {
    console.error('Error checking permissions:', error);
    return false;
  }
}

export async function getUserPermissions(): Promise<number[]> {
  try {
    const user = await getUserFromToken();
    return user?.permissions || [];
  } catch (error) {
    console.error('Error getting user permissions:', error);
    return [];
  }
}
